import { CircleHelp } from 'lucide-react';
import type { MouseEvent, ReactElement } from 'react';
import type { PlatformBridge } from '@slate/core';

/** Public help base URL. Anchors hang off this for contextual deep-links. */
export const HELP_URL_BASE = 'https://slateeditor.com/help';

interface Props {
  platform: PlatformBridge;
  /**
   * Anchor path after `/help/`. May include `#section`. Examples:
   *   "files#autosave"  →  https://slateeditor.com/help/files#autosave
   *   ""                →  https://slateeditor.com/help
   */
  anchor: string;
  /**
   * Accessible label for the icon-only button. Should describe the destination,
   * e.g. "Learn about autosave".
   */
  label: string;
  /** Render style: a small icon button (default) or an inline text link. */
  variant?: 'icon' | 'link';
  /** Optional text shown next to or as the link. */
  text?: string;
}

/**
 * Small "?" button that opens a contextual help page in the user's browser.
 * Uses the platform bridge so the same component works in the desktop and web
 * builds.
 */
export function HelpLink({
  platform,
  anchor,
  label,
  variant = 'icon',
  text,
}: Props): ReactElement {
  const url = anchor ? `${HELP_URL_BASE}/${anchor}` : HELP_URL_BASE;

  const onClick = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    void platform.openExternal(url).catch((err) => {
      console.debug('[slate] help link failed to open', url, err);
    });
  };

  if (variant === 'link') {
    return (
      <button
        type="button"
        className="mp-help-link"
        aria-label={label}
        onClick={onClick}
      >
        <CircleHelp size={12} aria-hidden strokeWidth={2} />
        <span>{text ?? 'Learn more'}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="mp-help-icon"
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <CircleHelp size={13} aria-hidden strokeWidth={2} />
    </button>
  );
}
